import React, { useEffect,useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal, OverlayTrigger, Popover } from "react-bootstrap";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, {
  PaginationProvider,
} from "react-bootstrap-table2-paginator";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../../_redux/users/usersActions";
import * as crud from "../../../_redux/users/usersCrud";

import * as uiHelpers from "../UsersUIHelpers";
import {
  getSelectRow,
  getHandlerTableChange,
  NoRecordsFoundMessage,
  PleaseWaitMessage,
  sortCaret,
} from "../../../../../../_metronic/_helpers";
import * as columnFormatters from "./column-formatters";
import { getRowStyleByStatus } from "../../../_helpers/tableRowStatusColors";
import { Pagination } from "../../../../../../_metronic/_partials/controls";
import { useUsersUIContext } from "../UsersUIContext";

export function UsersTable() {
  const navigate = useNavigate();
  const location = useLocation();
  // Users UI Context
  const {user} = useSelector(state => state.auth);
  const userobject = user.userData;
  var superadmin = true;
  if (userobject.admin_role && userobject.admin_role !== 'superadmin' && userobject.is_admin) { superadmin = false; }

  const usersUIContext = useUsersUIContext();
  const usersUIProps = useMemo(() => {
    return {
      ids: usersUIContext.ids,
      setIds: usersUIContext.setIds,
      queryParams: usersUIContext.queryParams,
      setQueryParams: usersUIContext.setQueryParams,
      openEditUserPage: usersUIContext.openEditUserPage,
      openDeleteUserDialog: usersUIContext.openDeleteUserDialog,
    };
  }, [usersUIContext]);

  // Getting curret state of users list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.users }),
    shallowEqual
  );
  const delay = ms => new Promise(res => setTimeout(res, ms));

  const { totalCount, entities, listLoading } = currentState;
  const [processing, setProcessing] = useState('');
  const [isEdit, setisEdit] = useState('');
  const [emailValue, setEmailValue] = useState('');
  const [nameValue, setNameValue] = useState('');
  // Users Redux state
  const dispatch = useDispatch();

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  // Grace Period Modal State
  const [showGracePeriodModal, setShowGracePeriodModal] = useState(false);
  const [selectedUserForGracePeriod, setSelectedUserForGracePeriod] = useState(null);
  const [graceStart, setGraceStart] = useState("");
  const [graceEnd, setGraceEnd] = useState("");
  const [savingGracePeriod, setSavingGracePeriod] = useState(false);

  // User Businesses Modal State
  const [showBusinessesModal, setShowBusinessesModal] = useState(false);
  const [selectedUserBusinesses, setSelectedUserBusinesses] = useState([]);
  const [selectedUserName, setSelectedUserName] = useState("");

  // Business Limit Modal State
  const [showBusinessLimitModal, setShowBusinessLimitModal] = useState(false);
  const [selectedUserForLimit, setSelectedUserForLimit] = useState(null);
  const [customLimit, setCustomLimit] = useState(5);
  const [savingLimit, setSavingLimit] = useState(false);

  const openBusinessLimitModal = (userRow) => {
    setSelectedUserForLimit(userRow);
    setCustomLimit(userRow.business_limit || 5);
    setSavingLimit(false);
    setShowBusinessLimitModal(true);
  };

  const handleSaveBusinessLimit = async () => {
    if (!selectedUserForLimit) return;
    const num = parseInt(customLimit, 10);
    if (isNaN(num) || num < 1) {
      alert("Please enter a valid positive number for business limit.");
      return;
    }
    setSavingLimit(true);
    try {
      const targetUserId = selectedUserForLimit.userid || selectedUserForLimit.id;
      await crud.updateBusinessLimitForUser(targetUserId, num);
      alert(`Business limit updated to ${num} successfully!`);
      setShowBusinessLimitModal(false);
      dispatch(actions.fetchUsers(usersUIProps.queryParams));
    } catch (err) {
      console.error(err);
      alert("Failed to update business limit. " + (err.response?.data?.message || err.message));
    } finally {
      setSavingLimit(false);
    }
  };

  const openBusinessesModal = (userRow) => {
    setSelectedUserName(userRow.name || userRow.email || "User");
    setSelectedUserBusinesses(userRow.businesses || []);
    setShowBusinessesModal(true);
  };

  const openSendEmailModal = (userRow) => {
    setSelectedUser(userRow);
    setEmailSubject("");
    setEmailMessage("");
    setSendingEmail(false);
    setShowEmailModal(true);
  };

  const openGracePeriodModal = (userRow) => {
    setSelectedUserForGracePeriod(userRow);
    setGraceStart(userRow.grace_period_start ? new Date(userRow.grace_period_start).toISOString().slice(0, 10) : "");
    setGraceEnd(userRow.grace_period_end ? new Date(userRow.grace_period_end).toISOString().slice(0, 10) : "");
    setSavingGracePeriod(false);
    setShowGracePeriodModal(true);
  };

  const handleSaveGracePeriod = async () => {
    if (!selectedUserForGracePeriod) return;
    setSavingGracePeriod(true);
    try {
      const targetUserId = selectedUserForGracePeriod.userid || selectedUserForGracePeriod.id;
      await crud.updateGracePeriodForUser(targetUserId, graceStart || null, graceEnd || null);
      alert("Grace period updated successfully!");
      setShowGracePeriodModal(false);
      dispatch(actions.fetchUsers(usersUIProps.queryParams));
    } catch (err) {
      console.error(err);
      alert("Failed to update grace period. " + (err.response?.data?.message || err.message));
    } finally {
      setSavingGracePeriod(false);
    }
  };

  const setPresetPeriod = (days) => {
    const start = graceStart ? new Date(graceStart) : new Date();
    const end = new Date(start.getTime() + days * 24 * 60 * 60 * 1000);
    setGraceStart(start.toISOString().slice(0, 10));
    setGraceEnd(end.toISOString().slice(0, 10));
  };

  const extendPeriodFromEnd = (days) => {
    let baseDate = graceEnd ? new Date(graceEnd) : new Date();
    if (isNaN(baseDate.getTime()) || baseDate.getTime() < Date.now()) {
      baseDate = new Date();
    }
    const newEnd = new Date(baseDate.getTime() + days * 24 * 60 * 60 * 1000);
    if (!graceStart) {
      setGraceStart(new Date().toISOString().slice(0, 10));
    }
    setGraceEnd(newEnd.toISOString().slice(0, 10));
  };

  const handleSendEmail = async () => {
    if (!selectedUser) return;
    setSendingEmail(true);
    try {
      await crud.sendCustomEmail(selectedUser.userid || selectedUser.id, selectedUser.email, emailSubject, emailMessage);
      alert("Email sent successfully!");
      setShowEmailModal(false);
    } catch (err) {
      console.error(err);
      alert("Failed to send email. " + (err.response?.data?.message || err.message));
    } finally {
      setSendingEmail(false);
    }
  };
  const updateStatus = async (id,status) => {
    setProcessing(id)
    await crud.updateStatusForUser(id,status);
    await delay(1000);
    dispatch(actions.fetchUsers(usersUIProps.queryParams));
    setProcessing('')
  };
  
  const editUser = async (row) => {
    setisEdit(row.id)
    setEmailValue(row.email)
    setNameValue(row.name)
  };
  
  const saveUser = async (row) => {
    setProcessing("save" + row.id)
    await crud.updateRecordForUser(row.id,nameValue,emailValue);
    await delay(1000);
    dispatch(actions.fetchUsers(usersUIProps.queryParams));
    setisEdit('')
    setProcessing('')
  };
  const rowStyle = (row) => ({
    ...getRowStyleByStatus(row, "status"),
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    color: "#181C32",
  });
  useEffect(() => {
    // clear selections list
    usersUIProps.setIds([]);
    // server call by queryParams
    dispatch(actions.fetchUsers(usersUIProps.queryParams));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersUIProps.queryParams, location.key, location.state, dispatch]);
  
  // Table columns
  let columns = [
    {
      attrs: { title: 'USER' },
      dataField: "name",
      text: "User",
      formatter: (cell, row) => {
        if (isEdit === row.id) {
          return (
            <div className="d-flex flex-column py-1" style={{ minWidth: "180px" }}>
              <input
                type="text"
                className="form-control form-control-sm mb-1 font-weight-bold"
                name="name"
                placeholder="Name"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
              />
              <input
                type="text"
                className="form-control form-control-sm"
                name="email"
                placeholder="Email"
                value={emailValue}
                onChange={(e) => setEmailValue(e.target.value)}
              />
            </div>
          );
        }
        return (
          <div className="d-flex flex-column py-1">
            <span className="font-weight-bolder text-dark font-size-sm" style={{ lineHeight: 1.25 }}>
              {row.name || "—"}
            </span>
            <span className="text-muted font-size-xs mt-1" style={{ lineHeight: 1.2 }}>
              {row.email || "—"}
            </span>
          </div>
        );
      },
      classes: "align-middle",
      headerClasses: "align-middle text-nowrap",
      sort: true,
      sortCaret: sortCaret,
    },
    {
      attrs: { title: 'BUSINESSES' },
      dataField: "business_count",
      text: "Businesses / Limit",
      formatter: (cell, row) => {
        const limit = row.business_limit || 5;
        const count = row.business_count || (row.businesses ? row.businesses.length : 0);
        const isNearLimit = count >= limit;
        return (
          <div className="d-inline-flex align-items-center text-nowrap">
            <button
              type="button"
              className="btn btn-sm btn-light-primary font-weight-bolder mr-2 py-1 px-3"
              onClick={() => openBusinessesModal(row)}
              title="Click to view businesses owned by this user"
            >
              <i className="fa fa-store mr-1 font-size-xs"></i>
              {count} {count === 1 ? "Business" : "Businesses"}
            </button>
            <span
              className="font-weight-bolder mr-1 px-2 py-1"
              style={{
                fontSize: "11px",
                borderRadius: "4px",
                display: "inline-block",
                backgroundColor: isNearLimit ? "#ffe2e5" : "#f3f6f9",
                color: isNearLimit ? "#f64e60" : "#181c32",
                border: isNearLimit ? "1px solid #f64e60" : "1px solid #dcdfe6",
              }}
              title={`Limit: ${count} of ${limit} businesses used`}
            >
              {count} / {limit}
            </span>
            <button
              type="button"
              className="btn btn-xs btn-icon btn-light-warning"
              title="Change business limit"
              onClick={() => openBusinessLimitModal(row)}
            >
              <i className="flaticon2-pen text-warning" style={{ fontSize: '10px' }}></i>
            </button>
          </div>
        );
      },
      classes: "align-middle text-nowrap",
      headerClasses: "align-middle text-nowrap",
      sort: true,
      sortCaret: sortCaret,
    },
    {
      attrs: { title: 'GRACE PERIOD' },
      dataField: "grace_period_start",
      text: "Grace Period",
      formatter: (cell, row) => {
        const hasGrace = Boolean(row.grace_period_start);
        let start = "-";
        let end = "-";
        let isExpired = false;
        let daysLeft = 0;

        if (hasGrace) {
          try {
            start = new Date(row.grace_period_start).toISOString().slice(0, 10);
            end = row.grace_period_end ? new Date(row.grace_period_end).toISOString().slice(0, 10) : "-";
            if (row.grace_period_end) {
              const diffMs = new Date(row.grace_period_end).getTime() - Date.now();
              daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
              isExpired = diffMs < 0;
            }
          } catch (e) {
            start = String(row.grace_period_start);
          }
        }

        if (!hasGrace) {
          return (
            <div
              className="d-inline-flex align-items-center"
              style={{ cursor: "pointer" }}
              onClick={() => openGracePeriodModal(row)}
              title="Click to set grace period"
            >
              <span
                className="font-weight-bolder px-2 py-1"
                style={{
                  fontSize: "11px",
                  borderRadius: "4px",
                  backgroundColor: "#f3f6f9",
                  color: "#5e6278",
                  border: "1px solid #dcdfe6",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                Not Started <i className="fa fa-plus-circle text-primary font-size-xs ml-1"></i>
              </span>
            </div>
          );
        }

        const popover = (
          <Popover id={`grace-popover-${row.id}`} style={{ minWidth: "270px", maxWidth: "330px", zIndex: 1060 }}>
            <Popover.Header
              as="h3"
              className="font-weight-bolder py-2 px-3 font-size-sm d-flex justify-content-between align-items-center"
              style={{ backgroundColor: "#f3f6f9", color: "#181c32", borderBottom: "1px solid #e4e6ef" }}
            >
              <span>Grace Period Info</span>
              <span
                className="font-weight-bold px-2 py-1"
                style={{
                  fontSize: "10px",
                  borderRadius: "3px",
                  backgroundColor: isExpired ? "#ffe2e5" : "#e8fbf8",
                  color: isExpired ? "#f64e60" : "#1bc5bd",
                }}
              >
                {isExpired ? "Expired" : "Active"}
              </span>
            </Popover.Header>
            <Popover.Body className="p-3" style={{ color: "#181c32" }}>
              <div className="font-size-xs mb-3">
                <div className="d-flex justify-content-between py-1 border-bottom">
                  <span className="text-muted font-weight-bold">Start Date:</span>
                  <span className="font-weight-bolder" style={{ color: "#181c32" }}>{start}</span>
                </div>
                <div className="d-flex justify-content-between py-1 border-bottom">
                  <span className="text-muted font-weight-bold">End Date:</span>
                  <span className="font-weight-bolder" style={{ color: "#181c32" }}>{end}</span>
                </div>
                <div className="d-flex justify-content-between py-1 border-bottom">
                  <span className="text-muted font-weight-bold">Duration:</span>
                  <span className="font-weight-bolder" style={{ color: "#181c32" }}>
                    {isExpired ? "Period Ended" : `${daysLeft} day(s) remaining`}
                  </span>
                </div>
                <div className="d-flex justify-content-between py-1">
                  <span className="text-muted font-weight-bold">Status:</span>
                  <span className="font-weight-bolder" style={{ color: isExpired ? "#f64e60" : "#1bc5bd" }}>
                    {isExpired ? "Expired" : "Active & In Grace"}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-light-primary font-weight-bold w-100 py-2"
                onClick={() => {
                  document.body.click();
                  openGracePeriodModal(row);
                }}
              >
                <i className="fa fa-calendar-alt mr-1 font-size-xs"></i>
                Update Grace Period
              </button>
            </Popover.Body>
          </Popover>
        );

        return (
          <OverlayTrigger trigger="click" rootClose placement="auto" overlay={popover}>
            <div
              className="d-flex flex-column align-items-start"
              style={{ cursor: "pointer" }}
              title="Click for grace period details"
            >
              <div className="d-inline-flex align-items-center">
                <span
                  className="font-weight-bolder px-2 py-1"
                  style={{
                    fontSize: "11px",
                    borderRadius: "4px",
                    backgroundColor: isExpired ? "#ffe2e5" : "#e8fbf8",
                    color: isExpired ? "#f64e60" : "#1bc5bd",
                    border: isExpired ? "1px solid #f64e60" : "1px solid #1bc5bd",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <i
                    className="fa fa-circle"
                    style={{
                      fontSize: "6px",
                      color: isExpired ? "#f64e60" : "#1bc5bd",
                    }}
                  ></i>
                  {isExpired ? "Expired" : daysLeft > 0 ? `Active (${daysLeft}d left)` : "Active"}
                  <i
                    className="fa fa-info-circle ml-1"
                    style={{
                      fontSize: "10px",
                      color: isExpired ? "#f64e60" : "#1bc5bd",
                      opacity: 0.85,
                    }}
                  ></i>
                </span>
              </div>
              <span
                className="font-weight-bold mt-1"
                style={{
                  fontSize: "11px",
                  color: "#5e6278",
                  lineHeight: 1.2,
                }}
              >
                {start} → {end}
              </span>
            </div>
          </OverlayTrigger>
        );
      },
      classes: "align-middle text-nowrap",
      headerClasses: "align-middle text-nowrap",
      sort: true,
      sortCaret: sortCaret,
    },
    {
      attrs: { title: 'STATUS' },
      dataField: "status",
      text: "Status",
      formatter: columnFormatters.StatusColumnFormatter,
      formatExtraData: {
        updateStatus: updateStatus,
        superadmin: superadmin,
        processing: processing,
      },
      classes: "align-middle text-nowrap media-action-class",
      headerClasses: "align-middle text-nowrap",
      sort: true,
      sortCaret: sortCaret,
    },
  ];
  if (superadmin) {
    //only push action column when user is super admin
    columns.push({
      attrs: { title: 'ACTION' },
      dataField: "action",
      text: "Actions",
      formatter: columnFormatters.ActionsColumnFormatter,
      formatExtraData: {
        editUser: editUser,
        saveUser: saveUser,
        superadmin: superadmin,
        isEdit: isEdit,
        processing: processing,
        setisEdit: setisEdit,
        openSendEmailModal: openSendEmailModal,
        openGracePeriodModal: openGracePeriodModal,
      },
      classes: "align-middle text-nowrap text-right pr-3",
      headerClasses: "align-middle text-nowrap text-right pr-3",
      style: {
        minWidth: "120px",
      },
    });
  }

  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: usersUIProps.queryParams.pageSize,
    page: usersUIProps.queryParams.pageNumber,
  };
  return (
    <>
      <Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Send Custom Email to {selectedUser?.name || "User"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group">
            <label className="font-weight-bold">To</label>
            <input
              type="text"
              className="form-control bg-light"
              value={selectedUser?.email || ""}
              disabled
            />
          </div>
          <div className="form-group mt-3">
            <label className="font-weight-bold text-dark">Subject</label>
            <input
              type="text"
              className="form-control border-secondary"
              placeholder="Enter email subject..."
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
            />
          </div>
          <div className="form-group mt-3">
            <label className="font-weight-bold text-dark">Message</label>
            <textarea
              className="form-control border-secondary"
              rows="5"
              placeholder="Type your custom message here..."
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light"
            onClick={() => setShowEmailModal(false)}
            disabled={sendingEmail}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSendEmail}
            disabled={sendingEmail || !emailSubject.trim() || !emailMessage.trim()}
          >
            {sendingEmail ? (
              <>
                <i className="fas fa-circle-notch fa-spin mr-2"></i> Sending...
              </>
            ) : (
              "Send Email"
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Grace Period Modal */}
      <Modal show={showGracePeriodModal} onHide={() => setShowGracePeriodModal(false)} centered size="md">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fa fa-calendar-alt text-warning mr-2"></i>
            Manage Grace Period for {selectedUserForGracePeriod?.name || "User"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group mb-3">
            <label className="font-weight-bold">User Email</label>
            <input
              type="text"
              className="form-control bg-light"
              value={selectedUserForGracePeriod?.email || ""}
              disabled
            />
          </div>

          <div className="row">
            <div className="col-md-6 form-group mb-3">
              <label className="font-weight-bold">Grace Period Start</label>
              <input
                type="date"
                className="form-control border-secondary"
                value={graceStart}
                onChange={(e) => setGraceStart(e.target.value)}
              />
            </div>
            <div className="col-md-6 form-group mb-3">
              <label className="font-weight-bold">Grace Period End</label>
              <input
                type="date"
                className="form-control border-secondary"
                value={graceEnd}
                onChange={(e) => setGraceEnd(e.target.value)}
              />
            </div>
          </div>

          <div className="mt-3 p-3 bg-light rounded border">
            <label className="font-weight-bold text-dark d-block mb-2 font-size-sm">Quick Presets / Extension:</label>
            <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => {
                  const now = new Date();
                  setGraceStart(now.toISOString().slice(0, 10));
                  const end = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
                  setGraceEnd(end.toISOString().slice(0, 10));
                }}
              >
                Start 30 Days Today
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-info"
                onClick={() => extendPeriodFromEnd(30)}
              >
                +30 Days
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-info"
                onClick={() => extendPeriodFromEnd(60)}
              >
                +60 Days
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-info"
                onClick={() => extendPeriodFromEnd(365)}
              >
                +1 Year
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger ml-auto"
                onClick={() => {
                  setGraceStart("");
                  setGraceEnd("");
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowGracePeriodModal(false)}
            disabled={savingGracePeriod}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveGracePeriod}
            disabled={savingGracePeriod}
          >
            {savingGracePeriod ? (
              <>
                <i className="fas fa-circle-notch fa-spin mr-2"></i> Saving...
              </>
            ) : (
              "Save Grace Period"
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* User Businesses Modal */}
      <Modal
        show={showBusinessesModal}
        onHide={() => setShowBusinessesModal(false)}
        centered
        size="xl"
      >
        <Modal.Header closeButton className="py-4 px-6 border-bottom">
          <Modal.Title className="d-flex align-items-center font-weight-bold">
            <span className="symbol symbol-35 symbol-light-primary mr-3 d-inline-flex align-items-center justify-content-center">
              <i className="fa fa-store text-primary font-size-h5"></i>
            </span>
            <span>Businesses Owned by <span className="text-primary">{selectedUserName}</span></span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-6">
          {selectedUserBusinesses && selectedUserBusinesses.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-head-custom table-vertical-center table-hover mb-0">
                <thead>
                  <tr className="text-uppercase text-dark-50 font-size-sm font-weight-bolder" style={{ letterSpacing: "0.5px" }}>
                    <th style={{ width: "80px" }} className="pl-4 py-3">ID</th>
                    <th style={{ minWidth: "240px" }} className="py-3">Business Name</th>
                    <th style={{ minWidth: "200px" }} className="py-3">Email</th>
                    <th style={{ minWidth: "120px" }} className="py-3">Status</th>
                    <th style={{ minWidth: "160px" }} className="py-3">Approved By</th>
                    <th style={{ width: "120px" }} className="text-right pr-4 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedUserBusinesses.map((biz) => (
                    <tr key={biz.id} className="border-bottom">
                      <td className="pl-4 py-4 font-weight-bold text-dark-75">#{biz.id}</td>
                      <td className="py-4">
                        <div className="d-flex align-items-center">
                          <span className="font-weight-bold text-dark font-size-base mr-3">
                            {biz.name}
                          </span>
                          {biz.is_purchased && (
                            <span
                              className="badge font-weight-bolder"
                              style={{
                                backgroundColor: "#fff4de",
                                color: "#b45309",
                                border: "1px solid #fcd34d",
                                padding: "4px 8px",
                                fontSize: "11px",
                                borderRadius: "4px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px"
                              }}
                            >
                              <i className="fa fa-crown mr-1" style={{ color: "#d97706", fontSize: "11px" }}></i>
                              Premium
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 text-muted">{biz.business_email || "-"}</td>
                      <td className="py-4">
                        <span
                          className={`badge badge-inline font-weight-bold ${
                            biz.status === "approved"
                              ? "badge-light-success text-success"
                              : biz.status === "pending"
                              ? "badge-light-warning text-warning"
                              : biz.status === "rejected"
                              ? "badge-light-danger text-danger"
                              : "badge-light-secondary"
                          }`}
                          style={{ padding: "6px 12px", fontSize: "12px" }}
                        >
                          {biz.status}
                        </span>
                      </td>
                      <td className="py-4">
                        {biz.approved_by_name ? (
                          <span className="font-weight-bold text-dark">{biz.approved_by_name}</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-right pr-4 py-4">
                        <button
                          type="button"
                          className="btn btn-sm btn-light-primary font-weight-bold px-4"
                          onClick={() => {
                            setShowBusinessesModal(false);
                            navigate(`/business/${biz.id}/edit`);
                          }}
                        >
                          <i className="fa fa-edit mr-1"></i> Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-10">
              <i className="fa fa-store text-muted font-size-h1 mb-3 d-block"></i>
              <h5 className="text-muted font-weight-bold">No businesses found for this user.</h5>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light"
            onClick={() => setShowBusinessesModal(false)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* Business Limit Modal */}
      <Modal
        show={showBusinessLimitModal}
        onHide={() => setShowBusinessLimitModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="font-weight-bold">
            <i className="flaticon2-layers text-primary mr-2"></i>
            Manage Business Limit
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUserForLimit && (
            <div>
              <div className="alert alert-custom alert-light-primary mb-4 p-3">
                <div className="alert-text font-weight-bold">
                  <strong>User:</strong> {selectedUserForLimit.name || "N/A"} ({selectedUserForLimit.email})
                  <br />
                  <strong>Current Businesses:</strong> {selectedUserForLimit.business_count || (selectedUserForLimit.businesses ? selectedUserForLimit.businesses.length : 0)}
                </div>
              </div>

              <div className="form-group">
                <label className="font-weight-bold">Maximum Allowed Businesses</label>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  className="form-control form-control-solid"
                  value={customLimit}
                  onChange={(e) => setCustomLimit(e.target.value)}
                  placeholder="e.g. 20, 50, 100"
                />
                <span className="form-text text-muted">
                  Default limit is 20 businesses. You can increase or adjust this limit for this specific user.
                </span>
              </div>

              <div className="d-flex flex-wrap gap-2 mt-3">
                <span className="font-weight-bold mr-2 align-self-center">Quick Presets:</span>
                {[5, 10, 20, 30, 50, 100].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    className={`btn btn-sm mr-2 mb-1 ${Number(customLimit) === preset ? "btn-primary" : "btn-light"}`}
                    onClick={() => setCustomLimit(preset)}
                  >
                    {preset}
                  </button>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light"
            onClick={() => setShowBusinessLimitModal(false)}
            disabled={savingLimit}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveBusinessLimit}
            disabled={savingLimit}
          >
            {savingLimit ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              "Save Limit"
            )}
          </button>
        </Modal.Footer>
      </Modal>

      <PaginationProvider pagination={paginationFactory(paginationOptions)}>
        {({ paginationProps, paginationTableProps }) => {
          return (
            <Pagination
              isLoading={listLoading}
              paginationProps={paginationProps}
            >
              <BootstrapTable
                wrapperClasses="table-responsive"
                classes="custom-res-table table table-head-custom table-head-bg table-vertical-center text-nowrap"
                bootstrap4
                bordered={false}
                remote
                keyField="id"
                data={entities === null ? [] : entities}
                columns={columns}
                defaultSorted={uiHelpers.defaultSorted}
                onTableChange={getHandlerTableChange(
                  usersUIProps.setQueryParams
                )}
                rowStyle={ rowStyle } 
                selectRow={getSelectRow({
                  entities,
                  ids: usersUIProps.ids,
                  setIds: usersUIProps.setIds,
                })}
                {...paginationTableProps}
              >
                <PleaseWaitMessage entities={entities} />
                <NoRecordsFoundMessage entities={entities} />
              </BootstrapTable>
            </Pagination>
          );
        }}
      </PaginationProvider>
    </>
  );
}
